/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
// [DLS PACKAGE TODO]: The below import violates our package hierarchy, DLS is a higher-level
// package then applications and can not import from higher-level packages. this needs to be
// refactored accordingly.
import {enableExportLimit, exporterCurrentSelector} from '@ion/applications/src/store/selectors/exporter-selectors';
import {translate} from '@ion/core/src/locale/locale';
import {UserNotificationDialog} from '../notifications/UserNotificationDialog';

interface ExportLimitNotificationProps {
  showDialog?: (dialog: boolean) => void;
  onExport?: () => void;
  exportCountLimit?: number;
  show?: boolean;
};

/**
 * The ExportLimitNotification provides the UI for prompting and cancelling an Export
 * Delete action.
 *
 * ```typescript
 *
 *    import {ExportLimitNotification} from '@ion/dls/src/editors/ExportLimitNotification';
 *
 *    return (
 *      <ExportLimitNotification show={show}
 *        showDialog={setShowPromptDialog}
 *        onExport={onExport} />
 *    );
 *
 * ```
 *
 * @param show When true, the asset delete restriction prompt will be displayed.
 * @param showDialog callback function which will be invoked if the user does some action.
 * @param onExport callback function which will be invoked if the user does some action on export button.
 */
export const ExportLimitNotification = ({
  showDialog,
  onExport,
  exportCountLimit,
  show = false
}: ExportLimitNotificationProps) => {
  const dispatch      = useDispatch();
  const {exportLimit} = useSelector(exporterCurrentSelector);
  const {limit}       = translate('common:export', {returnObjects: true, limit: exportCountLimit});
  const {shared}      = translate('common:editors', {returnObjects: true});

  useEffect(() => {
    if (exportLimit) {
      showDialog(false);
      onExport();
    }
  }, [exportLimit]);

  const onAction = useCallback(() => {
    dispatch(enableExportLimit(exportCountLimit));
  }, [exportCountLimit]);

  const onDismiss = useCallback(() => {
    showDialog(false);
  }, []);

  const getMessageBody = () => (
    <>
      <p>
        {limit.message}
      </p>
      <p>
        {limit.export}
      </p>
    </>
  );

  return (
    <UserNotificationDialog show={show}
      capitalize
      title={limit.title}
      message={getMessageBody()}
      actionLabel={shared.yesContinue}
      dismissLabel={shared.neverMind}
      onAction={onAction}
      onDismiss={onDismiss} />
  );
};

export default ExportLimitNotification;
